import { useEffect, useMemo, useState, memo, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatMoney, formatDate, getCurrency } from "@/lib/prefs";
import {
  addDays,
  isoDate,
  startOfWeek,
  computeShiftCost,
  totalLaborCost,
  totalLaborHours,
  startOfMonth,
  endOfMonth,
  monthGridStart,
  monthGridDays,
  addMonths,
} from "@/lib/labor";
import { ChevronLeft, ChevronRight, Plus, Loader2, Calendar, Trash2, X, Copy, GripVertical, Save, Printer } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { printRosterSchedule } from "@/lib/rosterPdf";
import { exportRosterCsv, exportRosterXlsx } from "@/lib/rosterExport";
import { exportRosterIndustryXlsx } from "@/lib/rosterIndustryExport";
import DataExportButton from "@/components/DataExportButton";
import { groupShiftsByDepartment, departmentLabel } from "@/lib/departments";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  closestCenter,
} from "@dnd-kit/core";

function ShiftForm({ open, onOpenChange, initial, staffList, onSaved, defaultDate }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial?.id;
  useEffect(() => {
    if (open) {
      setForm(initial ? { ...initial } : {
        staff_id: staffList[0]?.id || "",
        date: defaultDate || isoDate(new Date()),
        start_time: "16:00",
        end_time: "23:00",
        break_minutes: 30,
        notes: "",
      });
    }
  }, [open, initial, staffList, defaultDate]);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { ...form, break_minutes: Number(form.break_minutes) || 0 };
      if (isEdit) await api.patch(`/shifts/${initial.id}`, payload);
      else await api.post("/shifts", payload);
      toast.success(isEdit ? "Shift updated" : "Shift added");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="shift-form">
        <DialogHeader><DialogTitle className="font-display text-2xl">{isEdit ? "Edit shift" : "New shift"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Staff</Label>
            <Select value={form.staff_id} onValueChange={(v) => setForm({ ...form, staff_id: v })}>
              <SelectTrigger data-testid="shift-staff"><SelectValue /></SelectTrigger>
              <SelectContent>
                {staffList.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} • {s.role}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label>Date</Label><Input type="date" value={form.date || ""} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="shift-date" /></div>
          <div><Label>Start</Label><Input type="time" value={form.start_time || ""} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="shift-start" /></div>
          <div><Label>End</Label><Input type="time" value={form.end_time || ""} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="shift-end" /></div>
          <div className="col-span-2"><Label>Break (minutes)</Label><Input type="number" value={form.break_minutes || 0} onChange={(e) => setForm({ ...form, break_minutes: e.target.value })} data-testid="shift-break" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !form.staff_id} data-testid="shift-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DayDetailsDialog({ open, onOpenChange, date, shifts, staffById, onEdit, onAdd, onDelete, onDuplicate }) {
  if (!date) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="day-details">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {new Date(date).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2 max-h-[50vh] overflow-y-auto">
          {shifts.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No shifts scheduled.</p>
          ) : shifts.map((sh) => {
            const c = computeShiftCost(sh, staffById);
            return (
              <div
                key={sh.id}
                className="rounded-xl border border-border bg-secondary/40 p-3 flex items-center justify-between hover:bg-secondary transition-colors"
                data-testid={`day-shift-${sh.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{c.staff?.name || "-"}</div>
                  <div className="text-xs text-muted-foreground truncate">{c.staff?.role}</div>
                  <div className="text-xs tabular mt-1">{sh.start_time} to {sh.end_time} • {c.hours.toFixed(1)}h • {formatMoney(c.cost)}</div>
                </div>
                <div className="flex items-center gap-1 ml-2">
                  <Button size="sm" variant="outline" onClick={() => onEdit(sh)} data-testid={`day-shift-edit-${sh.id}`}>Edit</Button>
                  <Button size="sm" variant="ghost" onClick={() => onDuplicate(sh)} data-testid={`day-shift-dup-${sh.id}`} aria-label="Duplicate shift">
                    <Copy className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => onDelete(sh.id)} data-testid={`day-shift-del-${sh.id}`} aria-label="Remove shift">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}><X className="w-4 h-4 mr-1" />Close</Button>
          <Button onClick={() => onAdd(date)} data-testid="day-add-shift">
            <Plus className="w-4 h-4 mr-1" /> Add shift
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Roster() {
  const { user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("week"); // "week" | "fortnight" | "month"
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [monthAnchor, setMonthAnchor] = useState(() => startOfMonth(new Date()));
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [defaultDate, setDefaultDate] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null); // iso string for month view details
  // Rank hierarchy: operator's ordered list of kitchen titles. Loaded once
  // per session, refreshed whenever Settings dispatches the change event.
  // Both the roster grid and the industry Excel export sort staff by this
  // list, top down. See /app/backend/routes/rank_hierarchy.py.
  const [rankEntries, setRankEntries] = useState([]);
  // Buffered save: drag / duplicate / staff-drag / remove ONLY change local
  // state and push an op onto `pendingOps`. Nothing hits the DB until Save
  // Roster is clicked. Bulk ops (Copy last week/month, Clear this week)
  // still commit atomically server-side because they're batch operations
  // that are cleanest as a single call.
  const [pendingOps, setPendingOps] = useState([]);
  const [savingRoster, setSavingRoster] = useState(false);
  const hasPending = pendingOps.length > 0;
  const queueOp = (op) => setPendingOps((cur) => [...cur, op]);

  const range = useMemo(() => {
    if (view === "week") {
      return { from: isoDate(weekStart), to: isoDate(addDays(weekStart, 6)) };
    }
    if (view === "fortnight") {
      return { from: isoDate(weekStart), to: isoDate(addDays(weekStart, 13)) };
    }
    const gridStart = monthGridStart(monthAnchor);
    const days = monthGridDays(monthAnchor);
    return { from: isoDate(gridStart), to: isoDate(days[days.length - 1]) };
  }, [view, weekStart, monthAnchor]);

  const load = async () => {
    setLoading(true);
    try {
      const [s, sh] = await Promise.all([
        api.get("/staff"),
        api.get(`/shifts?date_from=${range.from}&date_to=${range.to}`),
      ]);
      setStaff(s.data);
      setShifts(sh.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [range.from, range.to]);

  // Rank hierarchy: fetch once on mount and re-fetch whenever Settings
  // fires the `cs:rank-hierarchy-changed` event. Falls back to a null
  // array (roster still renders, just without custom sort) on error.
  useEffect(() => {
    let cancelled = false;
    const fetchRanks = async () => {
      try {
        const { data } = await api.get("/rank-hierarchy");
        if (!cancelled) setRankEntries(data?.entries || []);
      } catch { /* silent: default sort applies */ }
    };
    fetchRanks();
    const onChange = () => fetchRanks();
    window.addEventListener("cs:rank-hierarchy-changed", onChange);
    return () => { cancelled = true; window.removeEventListener("cs:rank-hierarchy-changed", onChange); };
  }, []);

  // Sort helper: reorder any staff array using the rank hierarchy. Unknown
  // titles sink to the bottom in stable name order. Used by both the
  // Roster row rendering and the industry Excel export.
  const sortedStaff = useMemo(() => {
    const rankMap = new Map();
    rankEntries.forEach((e, i) => rankMap.set((e.title || "").trim().toLowerCase(), i));
    const order = (role) => {
      const v = rankMap.get((role || "").trim().toLowerCase());
      return v == null ? 9999 : v;
    };
    return [...staff].sort((a, b) => {
      const oa = order(a.role);
      const ob = order(b.role);
      if (oa !== ob) return oa - ob;
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [staff, rankEntries]);

  const staffById = useMemo(() => Object.fromEntries(staff.map((s) => [s.id, s])), [staff]);

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );
  // Fortnight view shows 14 continuous days from weekStart. Rendered as two
  // stacked 7-day grids to reuse the existing WeekGrid component so no
  // layout duplication is required.
  const fortnightDays = useMemo(
    () => Array.from({ length: 14 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );
  const secondWeekStart = useMemo(() => addDays(weekStart, 7), [weekStart]);
  const secondWeekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(secondWeekStart, i)),
    [secondWeekStart]
  );

  const shiftsByDay = useMemo(() => {
    const g = {};
    for (const sh of shifts) {
      if (!g[sh.date]) g[sh.date] = [];
      g[sh.date].push(sh);
    }
    return g;
  }, [shifts]);

  const totalHrs = totalLaborHours(shifts, staffById);
  const totalCost = totalLaborCost(shifts, staffById);

  // FOH vs BOH breakdown. Inferred from each staff member's free-text role
  // (Line Cook -> BOH, Server -> FOH, etc.). Only shows departments that
  // actually have shifts in the current view so an all-BOH kitchen never
  // sees an empty FOH card.
  const departmentTotals = useMemo(
    () => groupShiftsByDepartment(shifts, staffById, computeShiftCost),
    [shifts, staffById],
  );
  const departmentEntries = ["FOH", "BOH", "Other"]
    .map((code) => ({ code, ...departmentTotals[code] }))
    .filter((d) => d.shifts > 0);

  // Delete a shift with an INSTANT optimistic UI update. No native
  // `window.confirm()` (some browsers silently block/queue those) and no
  // page refresh. Card disappears the moment X is clicked. The DELETE
  // request is fired in parallel; if it fails the card is put back.
  // A 5-second Undo toast gives the chef a safety net without a modal.
  const removeShift = (id) => {
    const existing = shifts.find((s) => s.id === id);
    if (!existing) return;
    // Instant local removal. Nothing hits the DB until Save Roster.
    setShifts((cur) => cur.filter((s) => s.id !== id));
    if (id.startsWith("tmp-")) {
      // Never persisted: strip its create op from the queue instead of
      // queueing a delete (server has never seen this shift).
      setPendingOps((p) => p.filter((op) => !(op.type === "create" && op.tempId === id)));
    } else {
      queueOp({ type: "delete", id, snapshot: existing });
    }
    toast.success("Shift removed locally. Click Save Roster to commit.", {
      duration: 4000,
      action: {
        label: "Undo",
        onClick: () => {
          setShifts((cur) => [...cur, existing]);
          setPendingOps((p) => p.filter((op) => !(op.type === "delete" && op.id === id)));
        },
      },
    });
  };

  // Duplicate a shift onto the same day. Local-only until Save Roster.
  const duplicateShift = (shift) => {
    const tempId = `tmp-${(crypto.randomUUID?.() || Math.random().toString(36).slice(2))}`;
    const payload = {
      staff_id: shift.staff_id,
      date: shift.date,
      start_time: shift.start_time,
      end_time: shift.end_time,
      break_minutes: shift.break_minutes || 0,
    };
    const clone = { ...payload, id: tempId, _local: true };
    setShifts((cur) => [...cur, clone]);
    queueOp({ type: "create", tempId, payload });
    toast.success("Shift copied locally. Save Roster to commit.");
  };

  // Create a fresh shift when a staff chip is dropped on a day cell.
  const createShiftFromStaff = (staffId, date) => {
    const tempId = `tmp-${(crypto.randomUUID?.() || Math.random().toString(36).slice(2))}`;
    const payload = { staff_id: staffId, date, start_time: "16:00", end_time: "23:00", break_minutes: 30 };
    setShifts((cur) => [...cur, { ...payload, id: tempId, _local: true }]);
    queueOp({ type: "create", tempId, payload });
    const [y, m, d] = date.split("-").map(Number);
    const label = new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
    const name = staff.find((s) => s.id === staffId)?.name || "Staff";
    toast.success(`${name}: new shift on ${label} (pending). Save Roster to commit.`);
  };

  // Drag-move an existing shift to another date. Local-only until Save.
  const moveShiftToDate = (shiftId, newDate) => {
    const existing = shifts.find((s) => s.id === shiftId);
    if (!existing || existing.date === newDate) return;
    setShifts((cur) => cur.map((s) => (s.id === shiftId ? { ...s, date: newDate } : s)));
    if (shiftId.startsWith("tmp-")) {
      // Mutate the pending create op so it lands on the correct date.
      setPendingOps((p) => p.map((op) =>
        op.type === "create" && op.tempId === shiftId
          ? { ...op, payload: { ...op.payload, date: newDate } }
          : op
      ));
    } else {
      queueOp({ type: "move", id: shiftId, newDate });
    }
    const [y, m, d] = newDate.split("-").map(Number);
    const label = new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
    toast.success(`Moved to ${label} (pending). Save Roster to commit.`);
  };

  // Save every pending op to the DB in the order they happened. Deletes
  // and moves that target a tmp id are skipped (queue rewriting above
  // already handles those cases).
  const saveRoster = async () => {
    if (!hasPending) return;
    setSavingRoster(true);
    const ops = pendingOps;
    try {
      for (const op of ops) {
        if (op.type === "create") {
          await api.post("/shifts", op.payload);
        } else if (op.type === "delete") {
          await api.delete(`/shifts/${op.id}`);
        } else if (op.type === "move") {
          await api.patch(`/shifts/${op.id}`, { date: op.newDate });
        }
      }
      setPendingOps([]);
      await load();
      toast.success("Roster updated successfully: shifts are now live.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSavingRoster(false);
    }
  };

  // Warn before leaving the page with unsaved changes.
  useEffect(() => {
    if (!hasPending) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasPending]);

  const openNewShift = (isoDay) => {
    setEditing(null);
    setDefaultDate(isoDay);
    setDialogOpen(true);
  };

  const openEditShift = (sh) => {
    setEditing(sh);
    setDefaultDate(sh.date);
    setDialogOpen(true);
  };

  const [cloning, setCloning] = useState(null); // null | { kind: "week" | "month" | "clear-week" | "clear-month" }
  const [clearOpen, setClearOpen] = useState(false);  // Confirm dialog for Clear
  const copyLastWeek = async () => {
    const source = addDays(weekStart, -7);
    // Note if the target already has shifts (copy is additive, new UUIDs).
    const existingCount = weekDays.reduce(
      (n, d) => n + (shiftsByDay[isoDate(d)] || []).length, 0,
    );
    setCloning({ kind: "week" });
    try {
      const { data } = await api.post("/roster/clone-week", {
        source_week_start: isoDate(source),
        target_week_start: isoDate(weekStart),
      });
      if (data.count === 0) {
        toast.info("Last week had no shifts to copy.");
      } else {
        const onTop = existingCount > 0 ? ` (${existingCount} shift${existingCount === 1 ? "" : "s"} already present)` : "";
        toast.success(`Copied ${data.count} shift${data.count === 1 ? "" : "s"} from last week.${onTop}`);
        load();
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setCloning(null);
    }
  };

  const copyLastFortnight = async () => {
    const source = addDays(weekStart, -14);
    const existingCount = fortnightDays.reduce(
      (n, d) => n + (shiftsByDay[isoDate(d)] || []).length, 0,
    );
    setCloning({ kind: "fortnight" });
    try {
      const { data } = await api.post("/roster/clone-fortnight", {
        source_fortnight_start: isoDate(source),
        target_fortnight_start: isoDate(weekStart),
      });
      if (data.count === 0) {
        toast.info("Last fortnight had no shifts to copy.");
      } else {
        const onTop = existingCount > 0 ? ` (${existingCount} shift${existingCount === 1 ? "" : "s"} already present)` : "";
        toast.success(`Copied ${data.count} shift${data.count === 1 ? "" : "s"} from last fortnight.${onTop}`);
        load();
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setCloning(null);
    }
  };

  const copyLastMonth = async () => {
    // Build YYYY-MM strings from monthAnchor (target = current, source = one month prior).
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const targetMonth = fmt(monthAnchor);
    const sourceMonth = fmt(addMonths(monthAnchor, -1));
    const existingCount = Object.entries(shiftsByDay).reduce(
      (n, [iso, arr]) => (iso.startsWith(targetMonth) ? n + arr.length : n), 0,
    );

    setCloning({ kind: "month" });
    try {
      const { data } = await api.post("/roster/clone-month", {
        source_month: sourceMonth,
        target_month: targetMonth,
      });
      if (data.count === 0) {
        toast.info("Last month had no shifts to copy.");
      } else {
        const skippedMsg = data.skipped ? ` (${data.skipped} skipped: target month is shorter)` : "";
        const onTop = existingCount > 0 ? ` (${existingCount} shift${existingCount === 1 ? "" : "s"} already present)` : "";
        toast.success(`Copied ${data.count} shift${data.count === 1 ? "" : "s"} from last month.${skippedMsg}${onTop}`);
        load();
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setCloning(null);
    }
  };

  const isWeek = view === "week";
  const isFortnight = view === "fortnight";
  const isMonth = view === "month";

  // Delete every shift in the currently-viewed week, fortnight, or month.
  const clearCurrent = async () => {
    setClearOpen(false);
    const kind = isWeek ? "clear-week" : (isFortnight ? "clear-fortnight" : "clear-month");
    let from, to;
    if (isWeek) {
      from = isoDate(weekDays[0]);
      to = isoDate(weekDays[6]);
    } else if (isFortnight) {
      from = isoDate(fortnightDays[0]);
      to = isoDate(fortnightDays[13]);
    } else {
      from = isoDate(startOfMonth(monthAnchor));
      to = isoDate(endOfMonth(monthAnchor));
    }
    // Snapshot the current in-view shifts so we can restore them on Undo.
    const inViewSnapshot = shifts.filter((s) => s.date >= from && s.date <= to);
    if (inViewSnapshot.length === 0) {
      const noun = isWeek ? "week" : (isFortnight ? "fortnight" : "month");
      toast.info(`No shifts to clear in this ${noun}.`);
      return;
    }
    setCloning({ kind });
    // Optimistic clear so the UI empties immediately.
    setShifts((cur) => cur.filter((s) => !(s.date >= from && s.date <= to)));
    try {
      const { data } = await api.post("/roster/clear-range", { from_date: from, to_date: to });
      let undone = false;
      toast.success(`Cleared ${data.deleted} shift${data.deleted === 1 ? "" : "s"} from this ${isWeek ? "week" : (isFortnight ? "fortnight" : "month")}.`, {
        duration: 6000,
        action: {
          label: "Undo",
          onClick: async () => {
            undone = true;
            try {
              // Rehydrate every deleted shift. Backend assigns new ids, which
              // is fine: the visual result and cost math are identical.
              await Promise.all((data.shifts || []).map((sh) => api.post("/shifts", {
                staff_id: sh.staff_id,
                date: sh.date,
                start_time: sh.start_time,
                end_time: sh.end_time,
                break_minutes: sh.break_minutes || 0,
                hourly_rate_override: sh.hourly_rate_override ?? undefined,
                notes: sh.notes ?? undefined,
              })));
              load();
              toast.success("Schedule restored.");
            } catch { toast.error("Could not restore every shift."); }
          },
        },
      });
      // Void the closure ref to avoid linter warnings; state is in the toast.
      void undone;
    } catch (e) {
      // Roll back the optimistic clear.
      setShifts((cur) => [...cur, ...inViewSnapshot]);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setCloning(null);
    }
  };
  const monthLabel = monthAnchor.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const weekLabel = `${formatDate(weekDays[0])}: ${formatDate(weekDays[6])}`;
  const fortnightLabel = `${formatDate(fortnightDays[0])}: ${formatDate(fortnightDays[13])}`;
  const todayIso = isoDate(new Date());

  // Industry Excel export handler. Wraps the async exportRosterIndustryXlsx
  // so DataExportButton can hand it a plain callback. Uses the current
  // view's date range and the operator's rank hierarchy for row order.
  const doIndustryExport = async () => {
    let rangeStart, days;
    if (isWeek) { rangeStart = isoDate(weekStart); days = 7; }
    else if (isFortnight) { rangeStart = isoDate(weekStart); days = 14; }
    else {
      rangeStart = isoDate(startOfMonth(monthAnchor));
      const eom = endOfMonth(monthAnchor);
      days = Math.round((eom - startOfMonth(monthAnchor)) / (1000 * 60 * 60 * 24)) + 1;
    }
    try {
      await exportRosterIndustryXlsx({
        shifts,
        staff,
        rankEntries,
        rangeStart,
        days,
        restaurantName: user?.restaurant_name || "",
      });
      toast.success("Industry roster exported.");
    } catch (e) {
      toast.error("Could not build the industry export.");
      console.error(e);
    }
  };

  const handlePrintShare = () => {
    if (hasPending) {
      toast.info("You have unsaved changes. Save the roster to include them in the print.");
    }
    if (!staff.length) {
      toast.error("Add at least one team member before printing.");
      return;
    }
    printRosterSchedule({
      mode: isWeek ? "week" : "month",
      weekStart,
      monthAnchor,
      staff,
      shifts,
      restaurant: user?.restaurant_name || user?.name || "Kitchen",
      currency: getCurrency() || user?.currency || user?.preferences?.currency || "USD",
    });
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-7xl mx-auto space-y-6" data-testid="roster-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow flex items-center gap-2"><Calendar className="w-3 h-3" /> Roster</div>
          <h1 className="font-display text-3xl md:text-4xl leading-none mt-2">
            {isWeek ? "This week" : (isFortnight ? "This fortnight" : monthLabel)}
          </h1>
          <p className="text-muted-foreground mt-2 text-sm">{isWeek ? weekLabel : (isFortnight ? fortnightLabel : "Monthly overview")}</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <DataExportButton
            testId="roster-export"
            label="Export"
            disabled={loading || shifts.length === 0}
            onCsv={() => exportRosterCsv({
              shifts,
              staff,
              mode: isWeek ? "week" : (isFortnight ? "fortnight" : "month"),
              weekStart,
              monthAnchor,
            })}
            onXlsx={doIndustryExport}
            onPdf={handlePrintShare}
          />
          <Button
            onClick={handlePrintShare}
            variant="outline"
            data-testid="print-roster"
            className="gap-2"
          >
            <Printer className="w-4 h-4" />
            Print / Share
          </Button>
          <Button
            onClick={saveRoster}
            disabled={!hasPending || savingRoster}
            data-testid="save-roster"
            className="gap-2 gold-cta"
          >
            {savingRoster ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {hasPending ? `Save Roster (${pendingOps.length})` : "Save Roster"}
          </Button>
          <Tabs value={view} onValueChange={setView}>
            <TabsList data-testid="view-toggle">
              <TabsTrigger value="week" data-testid="view-week">Weekly</TabsTrigger>
              <TabsTrigger value="fortnight" data-testid="view-fortnight">Fortnight</TabsTrigger>
              <TabsTrigger value="month" data-testid="view-month">Monthly</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="flex items-center gap-2">
            {isWeek && (
              <>
                <Button variant="outline" size="sm" onClick={() => setWeekStart(addDays(weekStart, -7))} data-testid="week-prev"><ChevronLeft className="w-4 h-4" /></Button>
                <Button variant="outline" size="sm" onClick={() => setWeekStart(startOfWeek(new Date()))} data-testid="week-today">Today</Button>
                <Button variant="outline" size="sm" onClick={() => setWeekStart(addDays(weekStart, 7))} data-testid="week-next"><ChevronRight className="w-4 h-4" /></Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyLastWeek}
                  disabled={!!cloning}
                  data-testid="copy-last-week"
                  className="gap-1.5"
                >
                  {cloning?.kind === "week" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Copy className="w-3.5 h-3.5" />}
                  Copy last week
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setClearOpen(true)}
                  disabled={!!cloning}
                  data-testid="clear-current-week"
                  className="gap-1.5 text-destructive/90 hover:text-destructive hover:border-destructive/40"
                >
                  {cloning?.kind === "clear-week" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                  Clear this week
                </Button>
              </>
            )}
            {isFortnight && (
              <>
                <Button variant="outline" size="sm" onClick={() => setWeekStart(addDays(weekStart, -14))} data-testid="fortnight-prev"><ChevronLeft className="w-4 h-4" /></Button>
                <Button variant="outline" size="sm" onClick={() => setWeekStart(startOfWeek(new Date()))} data-testid="fortnight-today">Today</Button>
                <Button variant="outline" size="sm" onClick={() => setWeekStart(addDays(weekStart, 14))} data-testid="fortnight-next"><ChevronRight className="w-4 h-4" /></Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyLastFortnight}
                  disabled={!!cloning}
                  data-testid="copy-last-fortnight"
                  className="gap-1.5"
                >
                  {cloning?.kind === "fortnight" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Copy className="w-3.5 h-3.5" />}
                  Copy last fortnight
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setClearOpen(true)}
                  disabled={!!cloning}
                  data-testid="clear-current-fortnight"
                  className="gap-1.5 text-destructive/90 hover:text-destructive hover:border-destructive/40"
                >
                  {cloning?.kind === "clear-fortnight" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                  Clear this fortnight
                </Button>
              </>
            )}
            {isMonth && (
              <>
                <Button variant="outline" size="sm" onClick={() => setMonthAnchor(addMonths(monthAnchor, -1))} data-testid="month-prev"><ChevronLeft className="w-4 h-4" /></Button>
                <Button variant="outline" size="sm" onClick={() => setMonthAnchor(startOfMonth(new Date()))} data-testid="month-today">Today</Button>
                <Button variant="outline" size="sm" onClick={() => setMonthAnchor(addMonths(monthAnchor, 1))} data-testid="month-next"><ChevronRight className="w-4 h-4" /></Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyLastMonth}
                  disabled={!!cloning}
                  data-testid="copy-last-month"
                  className="gap-1.5"
                >
                  {cloning?.kind === "month" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Copy className="w-3.5 h-3.5" />}
                  Copy last month
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setClearOpen(true)}
                  disabled={!!cloning}
                  data-testid="clear-current-month"
                  className="gap-1.5 text-destructive/90 hover:text-destructive hover:border-destructive/40"
                >
                  {cloning?.kind === "clear-month" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                  Clear this month
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-2xl glass-card p-4">
          <div className="label-eyebrow">{isWeek ? "Weekly hours" : (isFortnight ? "Fortnight hours" : "Monthly hours")}</div>
          <div className="font-display text-2xl tabular mt-1" data-testid="week-hours">{totalHrs.toFixed(1)}h</div>
        </div>
        <div className="rounded-2xl glass-card p-4">
          <div className="label-eyebrow">{isWeek ? "Weekly labor" : (isFortnight ? "Fortnight labor" : "Monthly labor")}</div>
          <div className="font-display text-2xl tabular mt-1" data-testid="week-cost">{formatMoney(totalCost)}</div>
        </div>
        <div className="rounded-2xl glass-card p-4">
          <div className="label-eyebrow">Shifts</div>
          <div className="font-display text-2xl tabular mt-1">{shifts.length}</div>
        </div>
        <div className="rounded-2xl glass-card p-4">
          <div className="label-eyebrow">Team active</div>
          <div className="font-display text-2xl tabular mt-1">{staff.filter((s) => s.active).length}</div>
        </div>
      </div>

      {departmentEntries.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3" data-testid="department-breakdown">
          {departmentEntries.map((d) => (
            <div
              key={d.code}
              className="rounded-2xl glass-card p-4"
              data-testid={`department-card-${d.code}`}
            >
              <div className="flex items-center justify-between">
                <div className="label-eyebrow">{departmentLabel(d.code)}</div>
                <span className="text-[10px] tabular px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/25">
                  {d.code}
                </span>
              </div>
              <div className="flex items-baseline justify-between mt-2">
                <div>
                  <div className="font-display text-2xl tabular leading-none" data-testid={`department-hours-${d.code}`}>
                    {d.hours.toFixed(1)}h
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-1 tabular">
                    {d.shifts} shift{d.shifts === 1 ? "" : "s"} : {d.staffIds.size} {d.staffIds.size === 1 ? "person" : "people"}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] label-eyebrow">Labor</div>
                  <div className="font-medium tabular text-sm mt-1" data-testid={`department-cost-${d.code}`}>
                    {formatMoney(d.cost)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : isWeek ? (
        <WeekGrid
          days={weekDays}
          staffList={sortedStaff}
          shiftsByDay={shiftsByDay}
          staffById={staffById}
          onAdd={openNewShift}
          onEdit={openEditShift}
          onDelete={removeShift}
          onDuplicate={duplicateShift}
          onMove={moveShiftToDate}
          onCreateFromStaff={createShiftFromStaff}
        />
      ) : isFortnight ? (
        <div className="space-y-6" data-testid="fortnight-grid">
          <div className="rounded-2xl overflow-hidden">
            <div className="px-3 py-1.5 text-[0.65rem] tracking-[0.18em] uppercase font-semibold" style={{ background: "rgba(212,175,55,0.10)", color: "#e8cf9a", border: "1px solid rgba(212,175,55,0.25)", borderBottom: "none", borderRadius: "1rem 1rem 0 0" }} data-testid="fortnight-week1-band">
              Week 1: {formatDate(weekDays[0])} to {formatDate(weekDays[6])}
            </div>
            <WeekGrid
              days={weekDays}
              staffList={sortedStaff}
              shiftsByDay={shiftsByDay}
              staffById={staffById}
              onAdd={openNewShift}
              onEdit={openEditShift}
              onDelete={removeShift}
              onDuplicate={duplicateShift}
              onMove={moveShiftToDate}
              onCreateFromStaff={createShiftFromStaff}
            />
          </div>
          <div className="rounded-2xl overflow-hidden">
            <div className="px-3 py-1.5 text-[0.65rem] tracking-[0.18em] uppercase font-semibold" style={{ background: "rgba(212,175,55,0.10)", color: "#e8cf9a", border: "1px solid rgba(212,175,55,0.25)", borderBottom: "none", borderRadius: "1rem 1rem 0 0" }} data-testid="fortnight-week2-band">
              Week 2: {formatDate(secondWeekDays[0])} to {formatDate(secondWeekDays[6])}
            </div>
            <WeekGrid
              days={secondWeekDays}
              staffList={sortedStaff}
              shiftsByDay={shiftsByDay}
              staffById={staffById}
              onAdd={openNewShift}
              onEdit={openEditShift}
              onDelete={removeShift}
              onDuplicate={duplicateShift}
              onMove={moveShiftToDate}
              onCreateFromStaff={createShiftFromStaff}
            />
          </div>
        </div>
      ) : (
        <MonthGrid
          monthAnchor={monthAnchor}
          staffList={sortedStaff}
          shiftsByDay={shiftsByDay}
          staffById={staffById}
          todayIso={todayIso}
          onSelectDay={setSelectedDay}
          onMove={moveShiftToDate}
          onCreateFromStaff={createShiftFromStaff}
        />
      )}

      <ShiftForm
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initial={editing}
        staffList={staff}
        onSaved={load}
        defaultDate={defaultDate}
      />

      <DayDetailsDialog
        open={!!selectedDay}
        onOpenChange={(o) => !o && setSelectedDay(null)}
        date={selectedDay}
        shifts={selectedDay ? (shiftsByDay[selectedDay] || []) : []}
        staffById={staffById}
        onEdit={(sh) => { setSelectedDay(null); openEditShift(sh); }}
        onAdd={(iso) => { setSelectedDay(null); openNewShift(iso); }}
        onDelete={async (id) => { await removeShift(id); }}
        onDuplicate={(sh) => duplicateShift(sh)}
      />

      {/* Confirm dialog for Clear. Bulk destructive action deserves a
          real modal (Shadcn Dialog, not fragile native confirm). Shows
          how many shifts will be removed and offers a clear cancel. */}
      <Dialog open={clearOpen} onOpenChange={setClearOpen}>
        <DialogContent data-testid="clear-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Clear {isWeek ? "this week" : (isFortnight ? "this fortnight" : "this month")}?</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground space-y-2">
            <p>
              This removes every shift dated {isWeek ? formatDate(weekDays[0]) : (isFortnight ? formatDate(fortnightDays[0]) : formatDate(startOfMonth(monthAnchor)))}
              {" to "}
              {isWeek ? formatDate(weekDays[6]) : (isFortnight ? formatDate(fortnightDays[13]) : formatDate(endOfMonth(monthAnchor)))}.
            </p>
            <p className="tabular">
              <span className="font-medium text-foreground">
                {(() => {
                  const from = isoDate(isWeek ? weekDays[0] : (isFortnight ? fortnightDays[0] : startOfMonth(monthAnchor)));
                  const to = isoDate(isWeek ? weekDays[6] : (isFortnight ? fortnightDays[13] : endOfMonth(monthAnchor)));
                  return shifts.filter((s) => s.date >= from && s.date <= to).length;
                })()}
              </span>{" "}
              shift(s) will be removed. Undo is available for six seconds after clear.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setClearOpen(false)} data-testid="clear-cancel">Cancel</Button>
            <Button
              variant="destructive"
              onClick={clearCurrent}
              data-testid="clear-confirm"
              className="gap-2"
            >
              <Trash2 className="w-4 h-4" /> Clear {isWeek ? "week" : (isFortnight ? "fortnight" : "month")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Full-screen glass loading panel: shown while a clone operation is
          in-flight. Uses True-Glass blur so the roster beneath is still
          visible but not interactive. */}
      {cloning && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[16px]"
          data-testid="clone-loading-overlay"
          aria-live="polite"
          role="status"
        >
          <div className="rounded-2xl border border-white/20 bg-white/10 backdrop-blur-[28px] px-8 py-6 shadow-[0_30px_80px_-10px_rgba(0,0,0,0.7)] flex flex-col items-center gap-3 max-w-sm">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <div className="font-display text-lg">
              {cloning.kind === "week" && "Copying last week."}
              {cloning.kind === "month" && "Copying last month."}
              {cloning.kind === "clear-week" && "Clearing this week."}
              {cloning.kind === "clear-month" && "Clearing this month."}
            </div>
            <div className="text-sm text-muted-foreground text-center">
              {cloning.kind?.startsWith("clear")
                ? "Removing every shift in the current view. Undo will be available for six seconds."
                : "Deep copy in progress: every shift gets a fresh id. Do not close this tab."}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Draggable staff chip. Dropping it on a day cell creates a new shift
 * for that staff member on that date with defaults (16:00-23:00, 30 min
 * break). Data payload carries `type: "new-from-staff"` so the drop
 * handler can tell it apart from an existing shift move.
 */
function DraggableStaffChip({ staff }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `staff:${staff.id}`,
    data: { type: "new-from-staff", staffId: staff.id, staff },
  });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={[
        "text-xs px-2.5 py-1.5 rounded-full flex items-center gap-1.5 shrink-0",
        "border border-white/10 bg-white/[0.05] backdrop-blur-[8px]",
        "hover:bg-white/[0.1] cursor-grab active:cursor-grabbing select-none",
        isDragging ? "opacity-30" : "",
      ].join(" ")}
      data-testid={`staff-chip-${staff.id}`}
      title={`Drag onto a day to create a shift for ${staff.name}`}
    >
      <GripVertical className="w-3 h-3 text-white/25" />
      <span className="font-medium truncate max-w-[110px]">{staff.name}</span>
      <span className="text-muted-foreground truncate max-w-[70px] hidden md:inline">{staff.role}</span>
    </div>
  );
}

/**
 * Horizontal-scrolling strip of every active staff member. Sits above the
 * roster grid so a chef can grab a name and drop it onto any day.
 */
function StaffPalette({ staff }) {
  const active = staff.filter((s) => s.active !== false);
  if (active.length === 0) return null;
  return (
    <div className="rounded-2xl glass-card p-3" data-testid="staff-palette">
      <div className="label-eyebrow text-[0.55rem] mb-2">Drag a name onto a day to create a shift</div>
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        {active.map((s) => (
          <DraggableStaffChip key={s.id} staff={s} />
        ))}
      </div>
    </div>
  );
}

/**
 * A single shift card. Split into two regions so click/drag on the body
 * never fights with the action buttons:
 *
 *   [ grip + name + time + hours + $ ]   ← drag surface (listeners here)
 *   [ Duplicate ] [ Remove ]              ← action buttons (always visible)
 *
 * The outer div holds `setNodeRef` because dnd-kit needs a stable node
 * to measure. Listeners live only on the inner drag surface so tapping
 * an action button never triggers a drag.
 */
const DraggableShift = memo(function DraggableShift({ shift, staffById, onEdit, onDelete, onDuplicate }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: shift.id,
    data: { sourceDate: shift.date, shift },
  });
  const c = computeShiftCost(shift, staffById);
  return (
    <div
      ref={setNodeRef}
      className={[
        "shift-card text-xs rounded-lg group select-none relative",
        "border border-white/8 bg-white/[0.03] backdrop-blur-[8px]",
        "hover:border-white/12 transition-colors",
        isDragging ? "opacity-30" : "",
      ].join(" ")}
      data-testid={`shift-${shift.id}`}
    >
      {/* Drag surface. Whole body area moves the shift; tap opens edit. */}
      <div
        {...attributes}
        {...listeners}
        onClick={() => onEdit(shift)}
        role="button"
        tabIndex={0}
        className="p-2 pr-14 cursor-grab active:cursor-grabbing"
        aria-label={`Shift for ${c.staff?.name || "unassigned"} on ${shift.date}`}
      >
        <div className="flex items-start gap-1.5">
          <GripVertical className="w-3 h-3 text-white/25 mt-0.5 shrink-0" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <div className="font-medium truncate">{c.staff?.name || "-"}</div>
            <div className="text-muted-foreground truncate">{c.staff?.role}</div>
            <div className="tabular mt-1">{shift.start_time} to {shift.end_time}</div>
            <div className="flex items-center justify-between mt-1">
              <span className="text-muted-foreground tabular">{c.hours.toFixed(1)}h</span>
              <span className="font-medium tabular">{formatMoney(c.cost)}</span>
            </div>
          </div>
        </div>
      </div>
      {/* Action buttons sit OUTSIDE the drag surface so their clicks are
          never intercepted by the PointerSensor. Always visible so touch
          users can find them (no hover state on mobile). */}
      <div className="absolute top-1.5 right-1.5 flex items-center gap-0.5">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDuplicate(shift); }}
          className="w-6 h-6 rounded-md text-muted-foreground/60 hover:text-primary hover:bg-primary/10 inline-flex items-center justify-center transition-colors"
          aria-label={`Duplicate shift for ${c.staff?.name || "unassigned"}`}
          data-testid={`shift-duplicate-${shift.id}`}
        >
          <Copy className="w-3 h-3" />
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete(shift.id); }}
          className="w-6 h-6 rounded-md text-muted-foreground/60 hover:text-destructive hover:bg-destructive/10 inline-flex items-center justify-center transition-colors"
          aria-label={`Remove shift for ${c.staff?.name || "unassigned"}`}
          data-testid={`shift-remove-${shift.id}`}
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
});

/**
 * A single day column. Registered as a drop zone via `useDroppable`.
 * The `isOver` flag lights up the column border so the user knows the
 * drop is going to land.
 */
function DroppableDay({ isoKey, weekday, dayNum, dayShifts, staffById, dayCost, onAdd, onEdit, onDelete, onDuplicate }) {
  const { setNodeRef, isOver } = useDroppable({ id: isoKey });
  return (
    <div
      ref={setNodeRef}
      data-testid={`day-column-${isoKey}`}
      className={[
        "rounded-2xl glass-card overflow-hidden transition-colors",
        isOver ? "ring-2 ring-primary/70 bg-primary/[0.04]" : "",
      ].join(" ")}
    >
      <div className="px-3 py-2 border-b border-border flex items-center justify-between">
        <div>
          <div className="label-eyebrow text-[0.55rem]">{weekday}</div>
          <div className="font-display text-lg leading-none">{dayNum}</div>
        </div>
        <button
          className="text-muted-foreground hover:text-foreground"
          onClick={() => onAdd(isoKey)}
          data-testid={`day-add-${isoKey}`}
          aria-label="Add shift"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
      <div className="p-2 space-y-2 min-h-[140px]">
        {dayShifts.map((sh) => (
          <DraggableShift
            key={sh.id}
            shift={sh}
            staffById={staffById}
            onEdit={onEdit}
            onDelete={onDelete}
            onDuplicate={onDuplicate}
          />
        ))}
        {!dayShifts.length && (
          <p className="text-xs text-muted-foreground/60 text-center py-6">
            {isOver ? "Drop here" : "No shifts"}
          </p>
        )}
      </div>
      {dayShifts.length > 0 && (
        <div className="px-3 py-2 border-t border-border text-xs flex justify-between bg-secondary/30">
          <span className="text-muted-foreground">Day total</span>
          <span className="tabular font-medium">{formatMoney(dayCost)}</span>
        </div>
      )}
    </div>
  );
}

/**
 * The ghost card rendered inside `DragOverlay` for a staff-chip drag.
 * Uses the same True-Glass treatment as the shift ghost so the two
 * drag surfaces feel unified.
 */
function StaffChipGhost({ staff }) {
  return (
    <div
      className="text-xs px-3 py-2 rounded-full flex items-center gap-2 border border-white/20 bg-white/10 backdrop-blur-[24px] shadow-[0_20px_60px_-10px_rgba(0,0,0,0.6)] rotate-[1.5deg] pointer-events-none"
      data-testid="staff-chip-ghost"
    >
      <GripVertical className="w-3 h-3 text-white/30" />
      <span className="font-medium">{staff?.name}</span>
      <span className="text-muted-foreground">{staff?.role}</span>
    </div>
  );
}

/**
 * The ghost card rendered inside `DragOverlay`. Same content as the source
 * card but with the True-Glass blur cranked up so it feels detached from
 * the grid while the user drags it around.
 */
function ShiftGhost({ shift, staffById }) {
  const c = computeShiftCost(shift, staffById);
  return (
    <div
      className="text-xs rounded-lg p-2 w-[180px] border border-white/20 bg-white/10 backdrop-blur-[24px] shadow-[0_20px_60px_-10px_rgba(0,0,0,0.6)] rotate-[1.5deg] pointer-events-none"
      data-testid="shift-ghost"
    >
      <div className="font-medium truncate">{c.staff?.name || "-"}</div>
      <div className="text-muted-foreground truncate">{c.staff?.role}</div>
      <div className="tabular mt-1">{shift.start_time} to {shift.end_time}</div>
      <div className="flex items-center justify-between mt-1">
        <span className="text-muted-foreground tabular">{c.hours.toFixed(1)}h</span>
        <span className="font-medium tabular">{formatMoney(c.cost)}</span>
      </div>
    </div>
  );
}

function WeekGrid({ days, staffList, shiftsByDay, staffById, onAdd, onEdit, onDelete, onDuplicate, onMove, onCreateFromStaff }) {
  // Pointer needs to travel 5px before a drag starts. Anything shorter
  // still fires the card's onClick so single-tap-to-edit keeps working.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );
  const [activeId, setActiveId] = useState(null);
  const [activePayload, setActivePayload] = useState(null);
  const activeShift = activeId && !activeId.startsWith("staff:")
    ? Object.values(shiftsByDay).flat().find((s) => s.id === activeId)
    : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={(e) => { setActiveId(e.active.id); setActivePayload(e.active.data.current); }}
      onDragCancel={() => { setActiveId(null); setActivePayload(null); }}
      onDragEnd={(e) => {
        const payload = e.active.data.current;
        setActiveId(null); setActivePayload(null);
        const overId = e.over?.id;
        if (!overId) return;
        if (payload?.type === "new-from-staff") {
          onCreateFromStaff(payload.staffId, overId);
        } else {
          onMove(e.active.id, overId);
        }
      }}
    >
      <div className="space-y-4">
        <StaffPalette staff={staffList} />
        <div className="grid grid-cols-1 md:grid-cols-7 gap-3" data-testid="roster-grid">
          {days.map((d) => {
            const key = isoDate(d);
            const dayShifts = shiftsByDay[key] || [];
            const dayCost = totalLaborCost(dayShifts, staffById);
            return (
              <DroppableDay
                key={key}
                isoKey={key}
                weekday={d.toLocaleDateString(undefined, { weekday: "short" })}
                dayNum={d.getDate()}
                dayShifts={dayShifts}
                staffById={staffById}
                dayCost={dayCost}
                onAdd={onAdd}
                onEdit={onEdit}
                onDelete={onDelete}
                onDuplicate={onDuplicate}
              />
            );
          })}
        </div>
      </div>
      <DragOverlay dropAnimation={{ duration: 200, easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)" }}>
        {activeShift ? <ShiftGhost shift={activeShift} staffById={staffById} /> : null}
        {activePayload?.type === "new-from-staff" ? <StaffChipGhost staff={activePayload.staff} /> : null}
      </DragOverlay>
    </DndContext>
  );
}

/**
 * A single shift rendered as a mini-chip inside a Month cell. Same drag
 * semantics as the full WeekGrid card: 5px threshold separates click from
 * drag, delete affordances are handled elsewhere (Day Details modal). A
 * click bubbles up so the parent cell can open its details modal.
 */
const DraggableShiftChip = memo(function DraggableShiftChip({ shift, staffById }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: shift.id,
    data: { sourceDate: shift.date, shift },
  });
  const c = computeShiftCost(shift, staffById);
  const label = c.staff?.name ? c.staff.name.split(" ")[0] : "?";
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={(e) => { /* let the click bubble to the cell button */ }}
      className={[
        "text-[10px] leading-tight rounded px-1.5 py-0.5 truncate",
        "border border-white/8 bg-white/[0.05] backdrop-blur-[6px]",
        "hover:bg-white/[0.1] cursor-grab active:cursor-grabbing",
        isDragging ? "opacity-30" : "",
      ].join(" ")}
      data-testid={`month-chip-${shift.id}`}
    >
      <span className="font-medium">{label}</span>
      <span className="text-muted-foreground ml-1 tabular">{shift.start_time}</span>
    </div>
  );
});

function DroppableMonthCell({ isoKey, dayNum, inMonth, isToday, dayShifts, staffById, dayHours, onSelect }) {
  const { setNodeRef, isOver } = useDroppable({ id: isoKey });
  // Show up to 3 chips inline. Any more become a "+N more" overflow
  // pointing to the day-details modal.
  const visible = dayShifts.slice(0, 3);
  const overflow = dayShifts.length - visible.length;
  return (
    <div
      ref={setNodeRef}
      data-testid={`month-cell-${isoKey}`}
      onClick={() => onSelect(isoKey)}
      role="button"
      tabIndex={0}
      className={[
        "text-left border-b border-r border-border p-2 min-h-[120px] md:min-h-[140px] transition-colors relative cursor-pointer",
        inMonth ? "bg-transparent hover:bg-secondary/40" : "bg-secondary/10 text-muted-foreground/50 hover:bg-secondary/30",
        isToday ? "ring-1 ring-inset ring-primary/60" : "",
        isOver ? "ring-2 ring-inset ring-primary/70 bg-primary/[0.04]" : "",
      ].join(" ")}
    >
      <div className="flex items-center justify-between mb-1">
        <span className={[
          "font-display text-base leading-none tabular",
          isToday ? "text-primary" : "",
        ].join(" ")}>{dayNum}</span>
        {dayShifts.length > 0 && (
          <span
            className="text-[10px] tabular px-1.5 py-0.5 rounded-full bg-primary/15 text-primary border border-primary/30"
            data-testid={`month-cell-count-${isoKey}`}
          >
            {dayShifts.length}
          </span>
        )}
      </div>
      <div className="space-y-0.5">
        {visible.map((sh) => (
          <DraggableShiftChip key={sh.id} shift={sh} staffById={staffById} />
        ))}
        {overflow > 0 && (
          <div className="text-[10px] text-muted-foreground pt-0.5">+{overflow} more</div>
        )}
        {dayShifts.length === 0 && isOver && (
          <div className="text-[10px] text-muted-foreground/70">Drop here</div>
        )}
      </div>
      {dayShifts.length > 0 && (
        <div className="text-[10px] text-muted-foreground tabular absolute bottom-1 right-2">
          {dayHours.toFixed(1)}h
        </div>
      )}
    </div>
  );
}

function MonthGrid({ monthAnchor, staffList, shiftsByDay, staffById, todayIso, onSelectDay, onMove, onCreateFromStaff }) {
  const days = monthGridDays(monthAnchor);
  const targetMonth = monthAnchor.getMonth();
  const weekdayLabels = useMemo(() => {
    return days.slice(0, 7).map((d) => d.toLocaleDateString(undefined, { weekday: "short" }));
  }, [days]);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );
  const [activeId, setActiveId] = useState(null);
  const [activePayload, setActivePayload] = useState(null);
  const activeShift = activeId && !activeId.startsWith("staff:")
    ? Object.values(shiftsByDay).flat().find((s) => s.id === activeId)
    : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={(e) => { setActiveId(e.active.id); setActivePayload(e.active.data.current); }}
      onDragCancel={() => { setActiveId(null); setActivePayload(null); }}
      onDragEnd={(e) => {
        const payload = e.active.data.current;
        setActiveId(null); setActivePayload(null);
        const overId = e.over?.id;
        if (!overId) return;
        if (payload?.type === "new-from-staff") {
          onCreateFromStaff(payload.staffId, overId);
        } else {
          onMove(e.active.id, overId);
        }
      }}
    >
      <div className="space-y-4">
        <StaffPalette staff={staffList} />
        <div className="rounded-2xl glass-card overflow-x-auto" data-testid="month-grid">
          <div className="min-w-[640px]">
            <div className="grid grid-cols-7 border-b border-border bg-secondary/30">
              {weekdayLabels.map((lbl, i) => (
                <div key={i} className="px-3 py-2 label-eyebrow text-[0.55rem] text-center">{lbl}</div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {days.map((d) => {
                const key = isoDate(d);
                const dayShifts = shiftsByDay[key] || [];
                return (
                  <DroppableMonthCell
                    key={key}
                    isoKey={key}
                    dayNum={d.getDate()}
                    inMonth={d.getMonth() === targetMonth}
                    isToday={key === todayIso}
                    dayShifts={dayShifts}
                    staffById={staffById}
                    dayHours={totalLaborHours(dayShifts, staffById)}
                    onSelect={onSelectDay}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
      <DragOverlay dropAnimation={{ duration: 200, easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)" }}>
        {activeShift ? <ShiftGhost shift={activeShift} staffById={staffById} /> : null}
        {activePayload?.type === "new-from-staff" ? <StaffChipGhost staff={activePayload.staff} /> : null}
      </DragOverlay>
    </DndContext>
  );
}
